import { HttpApiError, HttpApiGroup } from "@effect/platform";
import { PillTheme } from "@redotech/redo-storefront-common/pill-theme";
import { Schema } from "effect";
import { endpoint } from "../auth/endpoint";
import { DataIntegrityError, DatabaseError } from "../auth/errors";

import {
  AccountId,
  ShopperAiTagGroupId,
  ShopperAiTagId,
} from "../ids/definitions";

export const TagResponse = Schema.Struct({
  id: ShopperAiTagId,
  accountId: AccountId,
  name: Schema.String,
  pillTheme: Schema.NullOr(Schema.Enums(PillTheme)),
  tagGroupId: ShopperAiTagGroupId,
});
export type TagResponse = typeof TagResponse.Type;

export const TagGroupResponse = Schema.Struct({
  id: ShopperAiTagGroupId,
  accountId: AccountId,
  name: Schema.String,
  description: Schema.NullOr(Schema.String),
  allowMultipleTags: Schema.Boolean,
  requiredTagBeforeClose: Schema.Boolean,
});
export type TagGroupResponse = typeof TagGroupResponse.Type;

const GetAccountTagsResponse = Schema.Struct({
  tags: Schema.Array(TagResponse),
  tagGroups: Schema.Array(TagGroupResponse),
});

const TagGroupWritableFields = {
  name: Schema.String,
  description: Schema.NullOr(Schema.String),
  allowMultipleTags: Schema.Boolean,
  requiredTagBeforeClose: Schema.Boolean,
};

export const CreateTagGroupPayload = Schema.Struct(TagGroupWritableFields);
export type CreateTagGroupPayload = typeof CreateTagGroupPayload.Type;

export const UpdateTagGroupPayload = Schema.Struct(TagGroupWritableFields);
export type UpdateTagGroupPayload = typeof UpdateTagGroupPayload.Type;

const TagWritableFields = {
  tagGroupId: ShopperAiTagGroupId,
  name: Schema.String,
  pillTheme: Schema.NullOr(Schema.Enums(PillTheme)),
};

export const CreateTagPayload = Schema.Struct(TagWritableFields);
export type CreateTagPayload = typeof CreateTagPayload.Type;

export const UpdateTagPayload = Schema.Struct(TagWritableFields);
export type UpdateTagPayload = typeof UpdateTagPayload.Type;

const TagGroupResult = Schema.Struct({ tagGroup: TagGroupResponse });
const TagResult = Schema.Struct({ tag: TagResponse });

export const TagGroup = HttpApiGroup.make("tag")
  .add(
    endpoint.merchant
      .get("getAccountTags", "/tags")

      .addSuccess(GetAccountTagsResponse)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post("createTagGroup", "/tags/groups")

      .setPayload(CreateTagGroupPayload)
      .addSuccess(TagGroupResult)
      .addError(HttpApiError.BadRequest)
      .addError(HttpApiError.Conflict)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .patch("updateTagGroup", "/tags/groups/:id")

      .setPath(Schema.Struct({ id: ShopperAiTagGroupId }))
      .setPayload(UpdateTagGroupPayload)
      .addSuccess(TagGroupResult)
      .addError(HttpApiError.BadRequest)
      .addError(HttpApiError.Conflict)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .del("deleteTagGroup", "/tags/groups/:id")

      .setPath(Schema.Struct({ id: ShopperAiTagGroupId }))
      .addSuccess(Schema.Void)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post("createTag", "/tags/tags")

      .setPayload(CreateTagPayload)
      .addSuccess(TagResult)
      .addError(HttpApiError.BadRequest)
      .addError(HttpApiError.Conflict)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .patch("updateTag", "/tags/tags/:id")

      .setPath(Schema.Struct({ id: ShopperAiTagId }))
      .setPayload(UpdateTagPayload)
      .addSuccess(TagResult)
      .addError(HttpApiError.BadRequest)
      .addError(HttpApiError.Conflict)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .del("deleteTag", "/tags/tags/:id")

      .setPath(Schema.Struct({ id: ShopperAiTagId }))
      .addSuccess(Schema.Void)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .prefix("/api");
